import { AutocompleteInputComponent } from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { DatePickerInput } from '@delmonicos/shared-design-system-react/src/components/date-picker-input/date-picker-input';
import { ModeEnum } from '@delmonicos/shared-design-system-react/src/components/date-picker/date-picker';
import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { TextArea } from '@delmonicos/shared-design-system-react/src/components/text-area/text-area.component';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { TokenType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/enums/token/token-type.enum';
import { useCreateTokenFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/create-token.form-validation';
import { CreateTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/create-token.model';
import { WhitelistType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useEffect } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useChargePointAutocomplete } from '@app/feature/charge-point/use-charge-point-autocomplete';
import { useCustomerAutocomplete } from '@app/feature/customer/use-customer-autocomplete';
import { useNetworkAutocomplete } from '@app/feature/network/use-network-autocomplete';

const TokenCreateFormStep1CustomerSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateTokenModel, 'customerId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { customerId } = useCreateTokenFormValidation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { search, setSearch, selectedItem, autocompleteItems } =
    useCustomerAutocomplete(field.value);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-token.customer-id')}
      placeholder={t('delmonicos-shared-sdk.outputs-token.customer-id')}
      required={!!customerId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const TokenCreateFormStep1NetworkSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateTokenModel, 'networkId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { watch } = useFormContext<CreateTokenModel>();
  const { networkId } = useCreateTokenFormValidation();
  const { onChange, value } = field;

  // WATCHERS ////////////////////////////////////////////////////////////////

  const customerId = watch('customerId');

  useEffect(() => {
    if (value && !customerId) {
      onChange('');
    }
  }, [customerId, value, onChange]);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { search, setSearch, selectedItem, autocompleteItems } =
    useNetworkAutocomplete(customerId, field.value);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-token.network-id')}
      placeholder={t('delmonicos-shared-sdk.outputs-token.network-id')}
      required={!!networkId.required}
      disabled={field.disabled || !customerId}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const TokenCreateFormStep1ChargePointSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateTokenModel, 'chargePointId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { watch } = useFormContext<CreateTokenModel>();
  const { chargePointId } = useCreateTokenFormValidation();
  const { onChange, value } = field;

  // WATCHERS ////////////////////////////////////////////////////////////////

  const networkId = watch('networkId');

  useEffect(() => {
    if (value && !networkId) {
      onChange('');
    }
  }, [networkId, value, onChange]);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { search, setSearch, selectedItem, autocompleteItems } =
    useChargePointAutocomplete(networkId, field.value);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-token.charge-point-id')}
      placeholder={t('delmonicos-shared-sdk.outputs-token.charge-point-id')}
      required={!!chargePointId.required}
      disabled={field.disabled || !networkId}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

export const TokenCreateFormStep1ControllerInformationFragment =
  (): React.JSX.Element => {
    const { t } = useTranslation();
    const { control, watch } = useFormContext<CreateTokenModel>();
    const {
      type,
      expirationDate,
      whitelist,
      networkId,
      customerId,
      chargePointId,
      firstName,
      lastName,
      vehicle,
      comment,
      uid,
      visualNumber,
    } = useCreateTokenFormValidation();

    const isPaymentTerminal = watch('type') === TokenType.PAYMENT_TERMINAL;
    const requiresCustomerAndNetwork = !!customerId.required;

    return (
      <Card gap={GapEnum.L} direction="column" flex={1}>
        <AccentTitle
          icon="info-circle"
          text={t('$.token-create.step-1.information.title')}
        />

        <Stack direction="column" gap={GapEnum.M}>
          <Controller
            name="type"
            control={control}
            rules={type}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <Text fontSize={TextFontSize.Small} uppercase>
                  {t('delmonicos-shared-sdk.outputs-token.type')}{' '}
                  <Text color={TextColor.Error}>*</Text>
                </Text>
                <Dropdown
                  {...field}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value ?? '')}>
                  <Dropdown.Option hidden disabled key="" value="" />
                  {[TokenType.PAYMENT_TERMINAL, TokenType.FREE].map((ty) => (
                    <Dropdown.Option key={ty} value={ty}>
                      {ty}
                    </Dropdown.Option>
                  ))}
                </Dropdown>
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
          <Controller
            name="whitelist"
            control={control}
            rules={whitelist}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <Text fontSize={TextFontSize.Small} uppercase>
                  {t('delmonicos-shared-sdk.outputs-token.whitelist')}{' '}
                  <Text color={TextColor.Error}>*</Text>
                </Text>
                <Dropdown
                  {...field}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value ?? '')}>
                  <Dropdown.Option hidden disabled key="" value="" />
                  {Object.values(WhitelistType).map((ty) => (
                    <Dropdown.Option key={ty} value={ty}>
                      {ty}
                    </Dropdown.Option>
                  ))}
                </Dropdown>
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
          {requiresCustomerAndNetwork && (
            <>
              <Controller
                name="customerId"
                control={control}
                rules={customerId}
                shouldUnregister
                render={TokenCreateFormStep1CustomerSelection}
              />
              <Controller
                name="networkId"
                control={control}
                rules={networkId}
                shouldUnregister
                render={TokenCreateFormStep1NetworkSelection}
              />
            </>
          )}
          {isPaymentTerminal && (
            <Controller
              name="chargePointId"
              control={control}
              rules={chargePointId}
              shouldUnregister
              render={TokenCreateFormStep1ChargePointSelection}
            />
          )}
          <Controller
            name="expirationDate"
            control={control}
            rules={expirationDate}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <Text fontSize={TextFontSize.Small} uppercase>
                  {t('delmonicos-shared-sdk.outputs-token.expiration-date')}
                </Text>
                <DatePickerInput
                  mode={ModeEnum.Single}
                  selected={field.value ? new Date(field.value) : undefined}
                  onSelect={(value) => field.onChange(value)}
                />
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
          <Controller
            name="firstName"
            control={control}
            rules={firstName}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.first-name')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-token.first-name',
                )}
                required={!!firstName?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={lastName}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.last-name')}
                placeholder={t('delmonicos-shared-sdk.outputs-token.last-name')}
                required={!!lastName?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="vehicle"
            control={control}
            rules={vehicle}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.vehicle')}
                placeholder={t('delmonicos-shared-sdk.outputs-token.vehicle')}
                required={!!vehicle?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="comment"
            control={control}
            rules={comment}
            render={({ field, fieldState: { error } }) => (
              <Stack gap={GapEnum.XS} direction="column">
                <TextArea
                  label={t('delmonicos-shared-sdk.outputs-token.comment')}
                  placeholder={t('delmonicos-shared-sdk.outputs-token.comment')}
                  required={!!comment?.required}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value === '' ? '' : e.target.value);
                  }}
                />
                {error?.message && (
                  <Text color={TextColor.Error}>{error.message}</Text>
                )}
              </Stack>
            )}
          />
          <Controller
            name="uid"
            control={control}
            rules={uid}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.uid')}
                placeholder={t('delmonicos-shared-sdk.outputs-token.uid')}
                required={!!uid?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="visualNumber"
            control={control}
            rules={visualNumber}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-token.visual-number')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-token.visual-number',
                )}
                required={!!visualNumber?.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
        </Stack>
      </Card>
    );
  };

export const TokenCreateFormStep1Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} direction="column" flex={1}>
      <TokenCreateFormStep1ControllerInformationFragment />
    </Stack>
  );
};
